import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { QrCode, Search, ArrowRight } from 'lucide-react';
import { useQrScanner } from '../../hooks/useQrScanner';

const AuditScanPage = () => {
  const navigate = useNavigate();
  const [manualCode, setManualCode] = useState('');
  const [scannerReady, setScannerReady] = useState(false);
  const { start: startQr, stop: stopQr } = useQrScanner();

  useEffect(() => {
    startScanner();
    return () => { stopQr(); };
  }, []);

  const startScanner = async () => {
    try {
      await new Promise(r => setTimeout(r, 300));
      await startQr('audit-qr-reader', (decodedText) => handleScan(decodedText), { fps: 10, qrbox: { width: 280, height: 280 } });
      setScannerReady(true);
    } catch (err) {
      console.error("Scanner error:", err);
    }
  };

  const stopScanner = async () => {
    await stopQr();
    setScannerReady(false);
  };

  const handleScan = (text) => {
    stopScanner();
    // Parse QR format: EQ:{uuid}, URL with /e/, or direct code
    let code = text;
    const parts = text.split(':');
    if (parts.length === 2 && parts[0] === 'EQ') {
      code = parts[1];
    } else if (text.includes('/e/')) {
      code = text.split('/e/').pop().split('?')[0];
    }
    toast.success(`Echipament gasit: ${code}`);
    navigate(`/audit/equipment/${code}`);
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      navigate(`/audit/equipment/${manualCode.trim()}`);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-lg mx-auto pb-24 lg:pb-8">
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold text-slate-900">Scanare Echipament</h1>
        <p className="text-sm text-slate-600 mt-1">
          Scaneaza codul QR de pe echipament pentru a vedea istoricul
        </p>
      </div>

      {/* QR Scanner */}
      <Card className="border-0 shadow-sm mb-4">
        <CardContent className="p-4">
          <div className="relative rounded-xl overflow-hidden bg-slate-900" style={{ minHeight: '320px' }}>
            <div id="audit-qr-reader" className="w-full" />
            {!scannerReady && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <QrCode className="w-16 h-16 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Se porneste camera...</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual Code Entry */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-sm text-slate-600 mb-3">Sau introdu codul manual:</p>
          <form onSubmit={handleManualSearch} className="flex gap-2">
            <Input
              placeholder="Ex: EQ-001"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              className="h-12 text-base"
              data-testid="manual-code-input"
            />
            <Button type="submit" className="h-12 bg-emerald-600 hover:bg-emerald-700" data-testid="manual-search-btn">
              <ArrowRight className="w-5 h-5" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditScanPage;
